import { ILocalizedOption } from "entities";
import { FORM_FLOW, FORM_SECTION, TimeSlotInfo } from "entities/Form";
import i18n from "i18n/i18n";
import moment from "moment-timezone";

export const getApiLangParam = (languageCode: string) => {
  switch (languageCode) {
    case "zh-CN":
      return "sc";
    case "zh-HK":
    case "zh-TW":
      return "tc";
    default:
      return "en"; // Default to English if the language code doesn't match
  }
};

export const getAmcOnlineBookingUrl = (languageCode: string) => {
  switch (getApiLangParam(languageCode)) {
    case "tc":
      return "https://amc.hkbh.org.hk/tc/online-booking/";
    case "sc":
      return "https://amc.hkbh.org.hk/sc/online-booking/";
    default:
      return "https://amc.hkbh.org.hk/online-booking/";
  }
};

export const convertWeekdayToChinese = (weekDay: string) => {
  const weekdaysMap: { [key: string]: string } = {
    Sunday: "日",
    Monday: "一",
    Tuesday: "二",
    Wednesday: "三",
    Thursday: "四",
    Friday: "五",
    Saturday: "六",
  };

  return weekdaysMap[weekDay] || null;
};

export const formatTime = (dateInput: string | Date) => {
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };

  const formatter = new Intl.DateTimeFormat(i18n.language, options);
  const parts = formatter.formatToParts(date);

  let timePart = "";

  parts.forEach((part) => {
    switch (part.type) {
      case "hour":
        const hour = parseInt(part.value, 10);
        timePart += i18n.language.startsWith("zh")
          ? (hour < 12 ? "上午" : "下午") +
            (hour % 12 === 0 ? 12 : hour % 12) +
            ":"
          : (part.value.length === 1 ? "0" + part.value : part.value) + ":";
        break;
      case "minute":
        timePart += part.value.length === 1 ? "0" + part.value : part.value;
        break;
      case "dayPeriod":
        if (!i18n.language.startsWith("zh")) {
          timePart += " " + part.value;
        }
        break;
    }
  });

  return timePart;
};

export const formatDate = (dateInput: string | Date, includeTime?: boolean) => {
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    weekday: "long",
  };

  const formatter = new Intl.DateTimeFormat(i18n.language, options);
  const parts = formatter.formatToParts(date);

  let datePart = "";

  parts.forEach((part) => {
    switch (part.type) {
      case "year":
        datePart += i18n.language.startsWith("zh")
          ? part.value + "年"
          : part.value;
        break;
      case "month":
        datePart += i18n.language.startsWith("zh")
          ? part.value + "月"
          : (part.value.length === 1 ? "0" + part.value : part.value) + "/";
        break;
      case "day":
        datePart += i18n.language.startsWith("zh")
          ? part.value + "日"
          : (part.value.length === 1 ? "0" + part.value : part.value) + "/";
        break;
      case "weekday":
        datePart = i18n.language.startsWith("zh")
          ? datePart + " (" + part.value + ")"
          : part.value + ", " + datePart;
        break;
    }
  });

  return includeTime ? datePart + " " + formatTime(dateInput) : datePart;
};

export const areDatesSame = (date1: Date, date2: Date) =>
  date1.getFullYear() === date2.getFullYear() &&
  date1.getMonth() === date2.getMonth() &&
  date1.getDate() === date2.getDate();

export const isSameMonthYear = (date1: Date, date2: Date): boolean =>
  date1.getFullYear() === date2.getFullYear() &&
  date1.getMonth() === date2.getMonth();

export const formatTimeInMinutesSeconds = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return minutes > 0
    ? i18n.t("secondsAndMinutes", { minutes, seconds })
    : i18n.t("units.seconds", { count: seconds });
};

export const getOtpExpireMessage = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return minutes > 0
    ? i18n.t("otpSecondsAndMinutes", { minutes, seconds })
    : i18n.t("otpSeconds", { seconds });
};

export const tzStartDay = (date?: Date | string) => {
  return moment
    .tz(
      moment(date).startOf("day").format("YYYY-MM-DD HH:mm:ss"),
      moment.tz.guess()
    )
    .toDate();
};

export const tzEndDay = (date?: Date) => {
  return moment
    .tz(
      moment(date).endOf("day").format("YYYY-MM-DD HH:mm:ss"),
      moment.tz.guess()
    )
    .toDate();
};

export const getLocalizedValue = (option?: Omit<ILocalizedOption, "value">) => {
  if (!option) return;
  if (i18n.language === "zh-HK" || i18n.language === "zh-TW")
    return option.nameTc;
  if (i18n.language === "zh-CN") return option.nameSc;
  return option.name;
};

export const generateRandomString = (length = 32) => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length }, () =>
    characters.charAt(Math.floor(Math.random() * characters.length))
  ).join("");
};

function decodeBase64ToArrayBuffer(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

export async function constructSecurePayload(
  payloadObj: any,
  serverPublicKeyBase64: string
) {
  const serverPublicKey = decodeBase64ToArrayBuffer(serverPublicKeyBase64);
  const symmetricKey = crypto.getRandomValues(new Uint8Array(32));
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const nonce = crypto.getRandomValues(new Uint8Array(16)).join("");

  const encoder = new TextEncoder();
  const payloadWithNonce = JSON.stringify({
    payload: JSON.stringify(payloadObj),
    nonce,
  });
  const aesKey = await crypto.subtle.importKey(
    "raw",
    symmetricKey,
    { name: "AES-CBC" },
    false,
    ["encrypt"]
  );
  const encryptedPayload = await crypto.subtle.encrypt(
    { name: "AES-CBC", iv: iv },
    aesKey,
    encoder.encode(payloadWithNonce)
  );

  const hmacKey = await crypto.subtle.importKey(
    "raw",
    symmetricKey,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const hmac = await crypto.subtle.sign("HMAC", hmacKey, encryptedPayload);

  const publicKey = await crypto.subtle.importKey(
    "spki",
    serverPublicKey,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
  const encryptedKey = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    new Uint8Array([...symmetricKey, ...iv])
  );

  // Step 5: Construct the request payload
  const requestPayload = {
    ek: btoa(String.fromCharCode(...new Uint8Array(encryptedKey))),
    ep: btoa(String.fromCharCode(...new Uint8Array(encryptedPayload))),
    hmac: btoa(String.fromCharCode(...new Uint8Array(hmac))),
    ts: new Date().toISOString(),
    n: nonce,
  };

  return requestPayload;
}

export async function verifySignatureWithPKCS1(
  publicKeyBase64: string,
  base64Data: string,
  signatureBase64: string
) {
  try {
    const publicKeyBytes = Uint8Array.from(atob(publicKeyBase64), (c) =>
      c.charCodeAt(0)
    );
    const dataBytes = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
    const signatureBytes = Uint8Array.from(atob(signatureBase64), (c) =>
      c.charCodeAt(0)
    );

    const publicKey = await crypto.subtle.importKey(
      "spki",
      publicKeyBytes.buffer,
      {
        name: "RSASSA-PKCS1-v1_5",
        hash: { name: "SHA-256" },
      },
      false,
      ["verify"]
    );

    const isValid = await crypto.subtle.verify(
      {
        name: "RSASSA-PKCS1-v1_5",
      },
      publicKey,
      signatureBytes,
      dataBytes
    );

    console.log(
      "isValid",
      isValid,

      "publicKey",
      publicKey,
      "signatureBytes",
      signatureBytes
    );

    return isValid;
  } catch (err) {
    console.error("Error during signature verification:", err);
    throw err;
  }
}

export const getFormOrder = (flow?: FORM_FLOW): FORM_SECTION[] =>
  flow === FORM_FLOW.DOCTOR_LAST
    ? [
        FORM_SECTION.DEPARTMENT,
        FORM_SECTION.SERVICE,
        FORM_SECTION.DATE_TIME,
        FORM_SECTION.PATIENT_FORM,
      ]
    : [
        FORM_SECTION.DEPARTMENT,
        FORM_SECTION.DOCTOR,
        FORM_SECTION.SERVICE,
        FORM_SECTION.DATE_TIME,
        FORM_SECTION.PATIENT_FORM,
      ];

export const resetDateToMidnight = (date: string | undefined) =>
  date ? moment(date).startOf("day").toDate() : undefined;

export const areAllTimeSlotsDisabledOrEmpty = (timeSlotDictionary?: {
  [key: string]: TimeSlotInfo;
}) =>
  !timeSlotDictionary
    ? true
    : Object.values(timeSlotDictionary).every(({ isDisabled, subGroups }) =>
        isDisabled || subGroups.length === 0
          ? true
          : subGroups.every(
              ({ fulfillSlot, roomSlot }) => fulfillSlot === 0 && roomSlot === 0
            )
      );
